import { create } from 'zustand';

/** Visibility state for feed track layers, keyed by layer id (== wsEvent type). */
interface LayerVisibilityState {
  /** Visibility flag per layer id. */
  visible: Map<string, boolean>;

  /** Set a layer's visibility explicitly. */
  setVisible: (layerId: string, visible: boolean) => void;

  /** Flip a layer's visibility. */
  toggle: (layerId: string) => void;

  /** Read a layer's current visibility (defaults to hidden). */
  isVisible: (layerId: string) => boolean;

  /** Set a layer's default visibility once, if not already known. */
  seed: (layerId: string, defaultVisible: boolean) => void;
}

/** Store driving which feed track layers are shown on the map. */
export const useLayerVisibilityStore = create<LayerVisibilityState>((set, get) => ({
  visible: new Map(),

  setVisible: (layerId, visible) =>
    set((state) => withEntry(state.visible, layerId, visible)),

  toggle: (layerId) =>
    set((state) => withEntry(state.visible, layerId, !(state.visible.get(layerId) ?? false))),

  isVisible: (layerId) => get().visible.get(layerId) ?? false,

  seed: (layerId, defaultVisible) =>
    set((state) =>
      state.visible.has(layerId)
        ? state
        : withEntry(state.visible, layerId, defaultVisible),
    ),
}));

/** Return new state with a single layer entry updated (immutable). */
function withEntry(
  current: Map<string, boolean>,
  layerId: string,
  visible: boolean,
): { visible: Map<string, boolean> } {
  const next = new Map(current);
  next.set(layerId, visible);
  return { visible: next };
}
